import React, { useRef, useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useFetchKeywordList } from "features/metadesk/redux/hooks";
import { useOutletContext } from "react-router-dom";
import { Button, COLOR, SIZE } from "@metacrm/metacrm-material-ui/dist/Button";
import { useDispatch } from "react-redux";
import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";
import _ from "lodash";
import { useSelector } from "react-redux";
import { selectRemindBarShow } from "../redux/globalState/globalState.selector";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import PopperMenu from "@metacrm/metacrm-material-ui/dist/PopperMenu";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import useGetKeywordTable from "./hooks/useGetKeywordTable";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import useNavigateEntity from "hooks/useNavigateEntity";
import AddKeywordModal from "./components/AddKeywordModal";
import { useIntl } from "react-intl";

const KeywordMonitoring = ({}) => {
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const remindBarShow = useSelector(selectRemindBarShow);
  const [openModal, setOpenModal] = useState("");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const {
    keywordListFilter: filter,
    keywordList,
    keywordListCount,
    updateKeywordListFilter,
    fetchKeywordList,
  } = useFetchKeywordList();

  const { navigateEntity } = useNavigateEntity();

  const { page, orderData, search } = filter;

  const { headers, renderContents, anchorEl, actionsList, handleCloseActions } =
    useGetKeywordTable();

  useEffect(() => {
    getKeywordList();
  }, [filter]);

  const abortControllerRef = useRef(new AbortController());

  const getKeywordList = async () => {
    abortControllerRef.current.abort(); // Cancel any ongoing request
    abortControllerRef.current = new AbortController(); // Create a new AbortController
    try {
      setIsLoading(true);
      await fetchKeywordList({
        entityName,
        filter,
        signal: abortControllerRef.current.signal,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const { sort, order } = orderData;
    const isAsc = sort === property && order === 1;
    updateKeywordListFilter({
      ...filter,
      page: 1,
      orderData: {
        sort: property,
        order: isAsc ? -1 : 1,
      },
    });
  };

  const minusHeight = 203 + (remindBarShow ? 26 : 0);

  return (
    <Box>
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        mb="24px"
        className="fade-up"
      >
        <Box>
          <Box className="title">
            {formatMessage({ id: "global.keywordMonitoring.title" })}
          </Box>
          <Box className="description">
            {formatMessage({ id: "global.keywordMonitoring.subtitle" })}
          </Box>
        </Box>
        <Button onClick={() => setOpenModal("addKeyword")}>
          {formatMessage({ id: "global.btn.addKeyword" })}
        </Button>
      </Box>
      <Box className="fade-up-more">
        <Box
          display="flex"
          alignItems="flex-end"
          mb="8px"
          justifyContent="space-between"
        >
          <Box className="item-flex">
            <Box style={{ marginRight: 8 }}>
              <InputField
                size={SIZE.MEDIUM}
                onEnter={(v, e) => e.target.blur()}
                placeholder={formatMessage({
                  id: "global.placeholder.searchKeyword",
                })}
                sx={{ width: "360px" }}
                startAdornment={
                  <i
                    className="meta-crm-icon-ic_search font-size-16"
                    style={{ marginRight: 5, color: "#A5A5A5" }}
                  />
                }
                value={search}
                onChange={(e) =>
                  updateKeywordListFilter({
                    ...filter,
                    search: e.target.value,
                    page: 1,
                  })
                }
                noBorder
                onBlur={() => {
                  getKeywordList();
                }}
              />
            </Box>
          </Box>
          <Box fontSize={12} color="#222" fontWeight={400}>
            {keywordListCount} {formatMessage({ id: "global.unit.keyword" })}
          </Box>
        </Box>

        <Box style={{ position: "relative" }}>
          <CRMTable
            containerStyle={{
              maxHeight: `calc(100vh - ${minusHeight}px)`,
            }}
            rowData={keywordList}
            loadingIndicatorAmount={25}
            noDataTitle="No Keyword"
            onRequestSort={handleRequestSort}
            orderBy={orderData.sort}
            order={_.get(orderData, "order") === 1 ? "asc" : "desc"}
            headers={headers}
            contents={_.map(keywordList, (keyword, i) => {
              return renderContents(keyword);
            })}
            isLoading={isLoading}
            LoadingContent={<LoadingIcon />}
            noDataComponent={
              <EmptyIconStatus
                icon="table"
                wording="later"
                emptyTitle={formatMessage({
                  id: "emptyState.table.title",
                })}
                emptyDescription={formatMessage({
                  id: "emptyState.table.description",
                })}
              />
            }
            onRowClick={(keywordId) => {
              navigateEntity(
                `/kol/keywordMonitoring/analysis/${keywordId}?keyword=${
                  keywordList.find((keyword) => keyword._id === keywordId)
                    .keyword
                }`
              );
            }}
            page={page}
            setPage={(page) => updateKeywordListFilter({ ...filter, page })}
            dataCounts={keywordListCount}
            // leftFooter={
            //   <Box fontSize={12} color="#555">
            //     {formatMessage({ id: "kolModule.dataRefreshInfo" })}
            //   </Box>
            // }
          />
        </Box>
      </Box>

      {/* popper menus  */}
      <PopperMenu
        placement="bottom-end"
        open={Boolean(anchorEl)}
        onClose={handleCloseActions}
        anchorEl={anchorEl}
        itemList={actionsList}
      />

      {/* modals */}
      <AddKeywordModal
        open={openModal === "addKeyword"}
        handleClose={() => setOpenModal("")}
        entityName={entityName}
      />
    </Box>
  );
};

export default KeywordMonitoring;
